import { useCallback, useRef } from 'react';
import type {
  Dispatch,
  MouseEvent,
  MutableRefObject,
  RefObject,
  SetStateAction,
} from 'react';

/** Ref whose `current` is read for size only (e.g. exiting toast ids). */
type ReadonlySetRef = RefObject<ReadonlySet<string>>;

/**
 * Pointer-driven **hovering** for the toast viewport.
 *
 * The viewport uses `pointer-events: none` with `auto` on stacked items so layout
 * changes do not emit spurious `mouseleave`. When the pointer leaves, we confirm with
 * one frame of delay plus `elementsFromPoint` ({@link isPointInsideViewportSubtree}).
 *
 * {@link UseViewportPointerExpandedParams.deferPointerLeaveCollapseWhileRef} defers
 * pointer-leave collapse while exit animations are in flight (see {@link ToastViewport}).
 * {@link UseViewportPointerExpandedParams.pinHoverDuringToastExitRef} blocks clearing
 * `hovering` from subtree checks while any toast is exiting (Base UI `hasTransitioningToasts`).
 *
 * **Focused** expansion (`:focus-visible` + Tab-move hint) lives in {@link ToastViewport}.
 */
export function getHitStackFromClientPoint(x: number, y: number): Element[] {
  const { elementsFromPoint } = document;
  if (typeof elementsFromPoint !== 'function') {
    return [];
  }
  try {
    return elementsFromPoint.call(document, x, y);
  } catch {
    return [];
  }
}

function isPointInsideViewportSubtree(
  root: HTMLElement,
  x: number,
  y: number
): boolean {
  const stack = getHitStackFromClientPoint(x, y);
  for (let i = 0; i < stack.length; i++) {
    if (root.contains(stack[i])) {
      return true;
    }
  }
  return false;
}

export type UseViewportPointerExpandedParams = {
  /**
   * Same element that hosts `onMouseEnter` / `onMouseLeave` / `onMouseMove` (the toast
   * `role="region"` viewport). Subtree checks in this module use this ref so
   * `elementsFromPoint` verification matches native enter/leave boundaries once the viewport
   * has a measured `minHeight` (see {@link ToastViewport}).
   */
  viewportRef: RefObject<HTMLDivElement | null>;
  setHovering: Dispatch<SetStateAction<boolean>>;
  /** When false, pointer handlers are no-ops. */
  pointerTrackingActive: boolean;
  /**
   * When `true`, a deferred pointer leave that would collapse hover is held until
   * {@link UseViewportPointerExpandedReturn.flushPendingPointerLeaveCollapse}.
   */
  deferPointerLeaveCollapseWhileRef?: MutableRefObject<boolean>;
  /**
   * While `current.size > 0`, do not set `hovering` false from pointer-leave verification
   * (viewport `minHeight` / rects can shrink mid-exit).
   */
  pinHoverDuringToastExitRef?: ReadonlySetRef;
};

export type UseViewportPointerExpandedReturn = {
  onMouseEnter: (event: MouseEvent) => void;
  onMouseLeave: (event: MouseEvent) => void;
  /** Re-evaluate hover on `mousemove` while the cursor is over the viewport (same as enter). */
  onMouseMove: (event: MouseEvent) => void;
  /** Cancel deferred leave rAFs; call when the toast queue becomes empty. */
  cancelPendingPointerLeave: () => void;
  /** Clear pointer tracking refs after cancel. */
  resetPointerTracking: () => void;
  /**
   * Re-apply deferred pointer leave collapse after toast exit animations finish
   * (no-op if leave was not deferred).
   */
  flushPendingPointerLeaveCollapse: () => void;
  /**
   * If the last known pointer position is still inside the viewport subtree, set hovering true.
   * Used after exit flush so gaps / layout do not spuriously clear hover.
   */
  revalidateHoverIfPointerInsideViewport: () => void;
};

export function useViewportPointerExpanded({
  viewportRef,
  setHovering,
  pointerTrackingActive,
  deferPointerLeaveCollapseWhileRef,
  pinHoverDuringToastExitRef,
}: UseViewportPointerExpandedParams): UseViewportPointerExpandedReturn {
  const pointerInsideRef = useRef(false);
  const lastPointerClientRef = useRef<{ x: number; y: number } | null>(null);
  const pointerLeaveCheckTokenRef = useRef(0);
  const pointerLeaveRafRef = useRef(0);
  const pendingPointerLeaveCollapseRef = useRef(false);

  const syncHovering = useCallback(() => {
    setHovering(pointerInsideRef.current);
  }, [setHovering]);

  const applyPointerLeaveIfOutside = useCallback(() => {
    const exiting = pinHoverDuringToastExitRef?.current;
    if (exiting && exiting.size > 0) {
      return;
    }
    const root = viewportRef.current;
    const pt = lastPointerClientRef.current;
    if (!pt || !root) {
      pointerInsideRef.current = false;
      syncHovering();
      return;
    }
    if (!isPointInsideViewportSubtree(root, pt.x, pt.y)) {
      pointerInsideRef.current = false;
      syncHovering();
    }
  }, [pinHoverDuringToastExitRef, syncHovering, viewportRef]);

  const flushPendingPointerLeaveCollapse = useCallback(() => {
    if (!pendingPointerLeaveCollapseRef.current) {
      return;
    }
    pendingPointerLeaveCollapseRef.current = false;
    applyPointerLeaveIfOutside();
  }, [applyPointerLeaveIfOutside]);

  const revalidateHoverIfPointerInsideViewport = useCallback(() => {
    if (!pointerTrackingActive) {
      return;
    }
    const root = viewportRef.current;
    const pt = lastPointerClientRef.current;
    if (!root || !pt) {
      return;
    }
    if (!isPointInsideViewportSubtree(root, pt.x, pt.y)) {
      return;
    }
    pointerInsideRef.current = true;
    syncHovering();
  }, [pointerTrackingActive, syncHovering, viewportRef]);

  const cancelPendingPointerLeave = useCallback(() => {
    if (pointerLeaveRafRef.current) {
      cancelAnimationFrame(pointerLeaveRafRef.current);
      pointerLeaveRafRef.current = 0;
    }
  }, []);

  const handleMouseEnter = useCallback(
    (event: MouseEvent) => {
      if (!pointerTrackingActive) {
        return;
      }
      lastPointerClientRef.current = { x: event.clientX, y: event.clientY };
      cancelPendingPointerLeave();
      pendingPointerLeaveCollapseRef.current = false;
      pointerLeaveCheckTokenRef.current += 1;
      pointerInsideRef.current = true;
      syncHovering();
    },
    [cancelPendingPointerLeave, pointerTrackingActive, syncHovering]
  );

  const handleMouseLeave = useCallback(
    (event: MouseEvent) => {
      if (!pointerTrackingActive) {
        return;
      }
      lastPointerClientRef.current = { x: event.clientX, y: event.clientY };
      cancelPendingPointerLeave();
      const tokenAtLeave = pointerLeaveCheckTokenRef.current;
      pointerLeaveRafRef.current = requestAnimationFrame(() => {
        pointerLeaveRafRef.current = 0;
        if (pointerLeaveCheckTokenRef.current !== tokenAtLeave) {
          return;
        }
        const defer = deferPointerLeaveCollapseWhileRef?.current ?? false;
        if (!defer) {
          applyPointerLeaveIfOutside();
          return;
        }
        const root = viewportRef.current;
        const pt = lastPointerClientRef.current;
        if (!pt || !root) {
          pendingPointerLeaveCollapseRef.current = true;
          return;
        }
        if (!isPointInsideViewportSubtree(root, pt.x, pt.y)) {
          pendingPointerLeaveCollapseRef.current = true;
        }
      });
    },
    [
      applyPointerLeaveIfOutside,
      cancelPendingPointerLeave,
      deferPointerLeaveCollapseWhileRef,
      pointerTrackingActive,
      viewportRef,
    ]
  );

  const resetPointerTracking = useCallback(() => {
    cancelPendingPointerLeave();
    pendingPointerLeaveCollapseRef.current = false;
    pointerLeaveCheckTokenRef.current += 1;
    pointerInsideRef.current = false;
    lastPointerClientRef.current = null;
  }, [cancelPendingPointerLeave]);

  return {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onMouseMove: handleMouseEnter,
    cancelPendingPointerLeave,
    resetPointerTracking,
    flushPendingPointerLeaveCollapse,
    revalidateHoverIfPointerInsideViewport,
  };
}
